import React, { FunctionComponent } from 'react';

import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { TRANSLATION_NAMESPACE, COMPLETION_STATUS } from 'directory/listings/notification-modals/constants';
import { CompletionBannerProps } from 'directory/listings/notification-modals/types';
import { getTranslationPluralKey, capitalizeFirstLetter } from 'directory/listings/notification-modals/utils';
import 'directory/listings/notification-modals/components/CompletionBanner/CompletionBanner.scss';

const DownloadIconSVG = () => (
    <svg
        width="1rem"
        height="1rem"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="download-icon mr-2"
    >
        <path
            d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M4.66406 6.66406L7.9974 9.9974L11.3307 6.66406"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CompletionBanner: FunctionComponent<CompletionBannerProps> = ({
    scenario,
    profilesUpdated,
    profilesWithErrors,
    totalProfiles,
    field,
    onDownloadErrors,
}) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);

    const renderBannerContent = () => {
        switch (scenario) {
            case COMPLETION_STATUS.FULL_SUCCESS: {
                const fieldName = capitalizeFirstLetter(t(field || 'UNKNOWN'));

                return (
                    <div className="completion-banner completion-banner--success">
                        <div className="completion-banner__content">
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="completion-banner__icon completion-banner__icon--success"
                            />
                            <span className="completion-banner__text">
                                {t(getTranslationPluralKey('PROFILES_UPDATED_SUCCESSFULLY', totalProfiles), {
                                    count: totalProfiles,
                                    fieldName,
                                })}
                            </span>
                        </div>
                    </div>
                );
            }

            case COMPLETION_STATUS.PARTIAL_SUCCESS:
                return (
                    <div className="completion-banner completion-banner--partial">
                        <div className="completion-banner__content">
                            <i className="icon-exclamation-circle completion-banner__icon completion-banner__icon--partial" />
                            <span className="completion-banner__text">
                                {t(getTranslationPluralKey('PROFILES_PARTIALLY_UPDATED', profilesUpdated), {
                                    updated: profilesUpdated,
                                    errors: profilesWithErrors,
                                    count: profilesUpdated,
                                })}
                            </span>
                        </div>
                        {onDownloadErrors && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="completion-banner__download-btn"
                                onClick={onDownloadErrors}
                            >
                                <DownloadIconSVG />
                                {t('DOWNLOAD_ERROR_FILE')}
                            </Button>
                        )}
                    </div>
                );

            case COMPLETION_STATUS.FULL_ERROR:
                return (
                    <div className="completion-banner completion-banner--error">
                        <div className="completion-banner__content">
                            <i className="icon-exclamation-circle completion-banner__icon completion-banner__icon--error" />
                            <span className="completion-banner__text__error">
                                {t(getTranslationPluralKey('PROFILES_UPDATE_FAILED', profilesWithErrors), {
                                    errors: profilesWithErrors,
                                    count: profilesWithErrors,
                                })}
                            </span>
                        </div>
                        {onDownloadErrors && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="completion-banner__download-btn"
                                onClick={onDownloadErrors}
                            >
                                <DownloadIconSVG />
                                {t('DOWNLOAD_ERROR_FILE')}
                            </Button>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return <div className="completion-banner-wrapper">{renderBannerContent()}</div>;
};
